// src/App.js
import React, { useState, useContext } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { I18nProvider, I18nContext } from "./context/I18nContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ConfigPage from "./components/Config/ConfigPage";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Logs from "./pages/Logs";
import Users from "./pages/Users";
import Profile from "./pages/Profile";
import AddEditUser from "./pages/AddEditUser";
import Groups from "./pages/Groups";
import AddEditGroup from "./pages/AddEditGroup";
import Permissions from "./pages/Permissions";
import Events from "./pages/Events";
import AccessDenied from "./pages/AccessDenied";
import MainLayout from "./layouts/MainLayout";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSystemConfigWS } from "./hooks/useSystemConfigWS";
import { notify } from "./utils/notify";
import { useEffect } from "react";
import api, { getAssetUrl } from "./services/api";
import { SystemConfigProvider, useSystemConfig } from "./context/SystemConfigContext";
import { OfflineProvider } from "./context/OfflineContext";
import OfflineBanner from "./components/OfflineBanner";
import OfflineQueueReview from "./pages/OfflineQueueReview";
import AddEditEvent from "./pages/AddEditEvent";
import DentalViewer from "./pages/DentalViewer";
import Countries from "./pages/Countries";
import AddEditCountry from "./pages/AddEditCountry";
import Zones from "./pages/Zones";
import AddEditZone from "./pages/AddEditZone";
import Cities from "./pages/Cities";
import AddEditCity from "./pages/AddEditCity";
import GeoZones from "./pages/GeoZones";
import AddEditGeoZone from "./pages/AddEditGeoZone";
import Modules from "./pages/Modules";
import CustomFields from "./pages/CustomFields";
import Branches from "./pages/Branches";
import AddEditBranch from "./pages/AddEditBranch";
import Sections from "./pages/Sections";
import AddEditSection from "./pages/AddEditSection";
import Tables from "./pages/Tables";
import AddEditTable from "./pages/AddEditTable";
import Categories from "./pages/Categories";
import AddEditCategory from "./pages/AddEditCategory";
import Units from "./pages/Units";
import AddEditUnit from "./pages/AddEditUnit";
import Products from "./pages/Products";
import AddEditProduct from "./pages/AddEditProduct";
import Inventory from "./pages/Inventory";
import StockMovements from "./pages/StockMovements";
import Taxes from "./pages/Taxes";
import AddEditTax from "./pages/AddEditTax";
import TaxGroups from "./pages/TaxGroups";
import AddEditTaxGroup from "./pages/AddEditTaxGroup";
import Currencies from "./pages/Currencies";
import AddEditCurrency from "./pages/AddEditCurrency";
import CurrencyDenominations from "./pages/CurrencyDenominations";
import Customers from "./pages/Customers";
import AddEditCustomer from "./pages/AddEditCustomer";
import Suppliers from "./pages/Suppliers";
import AddEditSupplier from "./pages/AddEditSupplier";
import PurchaseOrders from "./pages/PurchaseOrders";
import AddPurchaseOrder from "./pages/AddPurchaseOrder";
import ViewPurchaseOrder from "./pages/ViewPurchaseOrder";
import StockTransfers from "./pages/StockTransfers";
import AddStockTransfer from "./pages/AddStockTransfer";
import ViewStockTransfer from "./pages/ViewStockTransfer";
import Shifts from "./pages/Shifts";
import Vouchers from "./pages/Vouchers";
import Reports from "./pages/Reports";
import Orders from "./pages/Orders";
import OrderDetail from "./pages/OrderDetail";
import KitchenDisplay from "./pages/KitchenDisplay";
import Checkout from "./pages/Checkout";
import Discounts from "./pages/Discounts";
import AddEditSale from "./pages/AddEditSale";
import BundleOffers from "./pages/BundleOffers";
import AddEditBundleOffer from "./pages/AddEditBundleOffer";
import Languages from "./pages/Languages";
import Translations from "./pages/Translations";
import ChartOfAccounts from "./pages/ChartOfAccounts";
import AddEditAccount from "./pages/AddEditAccount";
import JournalEntries from "./pages/JournalEntries";
import AddJournalEntry from "./pages/AddJournalEntry";
import ExpenseCategories from "./pages/ExpenseCategories";
import AddEditExpenseCategory from "./pages/AddEditExpenseCategory";
import Services from "./pages/Services";
import AddEditService from "./pages/AddEditService";
import Appointments from "./pages/Appointments";
import Expenses from "./pages/Expenses";
import AddExpense from "./pages/AddExpense";
import PrintAgents from "./pages/PrintAgents";
import Printers from "./pages/Printers";
import AddEditPrinter from "./pages/AddEditPrinter";
import LocalPrintSetup from "./pages/LocalPrintSetup";
import ReceiptTemplateSettings from "./pages/ReceiptTemplateSettings";
import PrintJobs from "./pages/PrintJobs";
import PrintBarcodes from "./pages/PrintBarcodes";
import PlatformApp from "./PlatformApp";
import MarketingApp from "./pages/Marketing/MarketingApp";

// isPlatformHost decides, at runtime, in the browser, which of this one
// static build's two completely separate app trees to mount — see
// PlatformApp.js's own doc comment on why that split exists at the
// component level rather than as one more branch inside this file's own
// giant <Routes> tree below. process.env.REACT_APP_PLATFORM_HOST is a
// LOCAL-DEV override only (a dev machine has no real "admin.posflect.com"
// DNS entry to visit) — every real deployment instead matches this exact
// build's own runtime hostname's leading label against "admin", the same
// convention this feature's own plan doc and platform_routes.go's own doc
// comment already name (deploy/posflect-provision-subdomain.sh's ROOT_DOMAIN
// is what the wildcard DNS record and every company's own subdomain are
// built from; "admin" is simply never a company's own subdomain — see
// platform.ValidateSubdomainFormat's reserved-word blocklist).
const PLATFORM_HOST = process.env.REACT_APP_PLATFORM_HOST;
const isPlatformHost = PLATFORM_HOST
  ? window.location.hostname === PLATFORM_HOST
  : window.location.hostname.split(".")[0] === "admin";

// isMarketingHost is this feature's own third branch — the root domain
// itself, posflect.com (and its "www" alias), gets MarketingApp instead of
// silently falling into TenantApp and 400ing on every API call with
// TENANT_NOT_FOUND (see this feature's own plan doc's Grounding section).
// Deliberately an EXPLICIT hostname check, not a generic "fewer than 3
// dot-labels" mirror of the backend's own subdomain-extraction logic —
// that generic rule would also match bare "localhost", which would break
// every developer's existing local TenantApp dev workflow (none of them
// visit a real posflect.com in local dev). REACT_APP_MARKETING_HOST is a
// LOCAL-DEV override only, same convention as REACT_APP_PLATFORM_HOST just
// above. Checked only when isPlatformHost is already false — "admin" must
// always win if somehow both matched.
const MARKETING_HOST = process.env.REACT_APP_MARKETING_HOST;
const isMarketingHost = !isPlatformHost && (
  MARKETING_HOST
    ? window.location.hostname === MARKETING_HOST
    : window.location.hostname === "posflect.com" || window.location.hostname === "www.posflect.com"
);


// react-toastify بيرندر التوستات بتاعته بره شجرة الصفحة العادية (portal)،
// وبيدعم prop اسمه rtl بيقلب مكان التوست ويحط dir="rtl" عليه تلقائيًا — أنضف
// حل من إننا نحاول نلعب في position يدويًا. لازم يكون كومبوننت منفصل (مش جوه
// App نفسها) عشان يقدر يقرأ I18nContext كـ "حفيد" للـ I18nProvider، مش
// كمكوّن بيرندرها هو نفسه.
function AppToastContainer() {
  const { languages, languageCode } = useContext(I18nContext);
  const isRTL = languages.find((l) => l.code === languageCode)?.direction === "rtl";
  return (
    <ToastContainer
      position="top-right"
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop
      closeOnClick
      pauseOnHover
      draggable
      theme="colored"
      rtl={isRTL}
    />
  );
}

// Keeps the browser tab's icon in sync with Config > Appearance's uploaded
// "favicon" setting — same reason AppToastContainer above is its own
// component rather than inline code in App: it needs to read
// SystemConfigContext, which only a child of <SystemConfigProvider> can
// do. systemSettings is populated by whichever page mounted first (Login
// or MainLayout — see their own loadSettings/public-config fetch), so this
// only needs to react to it changing, not fetch anything itself.
//
// Note: appearance.favicon (and appearance.logo, used in MainLayout's own
// sidebar) point at a file under /uploads, which — unlike the rest of
// /api/public-config — is gated behind a valid JWT (see
// middlewares.StaticAssetAuth's own doc comment). getAssetUrl attaches the
// caller's own token when one exists, so this works correctly once
// signed in; a signed-out visitor on /login has no token to attach, so the
// request 401s and the browser just silently keeps its default icon
// instead — not a crash, just not yet the customized one. Making the
// logo/favicon files themselves reachable pre-login would mean carving out
// a public exception in StaticAssetAuth, a deliberate security call for
// the user to make, not an implied part of "wire up the display".
function FaviconSync() {
  const { systemSettings } = useSystemConfig();
  useEffect(() => {
    const faviconPath = systemSettings?.appearance?.favicon;
    if (!faviconPath) return;

    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = getAssetUrl(faviconPath);
  }, [systemSettings]);

  return null;
}

// Keeps the browser tab title in sync with Config > General > System Name,
// the same way FaviconSync above keeps the tab icon in sync with the
// uploaded favicon. public/index.html ships a static "Posflect" <title> as
// the fallback shown before this runs (and for search engines/social
// previews, which never execute this JS) — this is what makes each
// deployment's tab actually show ITS configured name instead of that
// generic default, once systemSettings has loaded (from either MainLayout's
// authenticated /public-config fetch, or Login's guest one — see Login.js's
// own comment on why it re-fetches independently).
function TitleSync() {
  const { systemSettings } = useSystemConfig();
  useEffect(() => {
    const systemName = systemSettings?.general?.system_name;
    if (systemName) document.title = systemName;
  }, [systemSettings]);

  return null;
}

// Same idea as TitleSync, for the <meta name="description"> tag —
// Config > General > Meta Description (system_description), which the
// backend already resolves per-language the same way it does system_name
// (see applyTranslatedGeneralFields in config_handler.go), so this just
// needs to react to systemSettings changing, same as TitleSync/FaviconSync.
// Left empty by an admin, this is a no-op and the static description
// index.html ships stays exactly as-is.
function MetaDescriptionSync() {
  const { systemSettings } = useSystemConfig();
  useEffect(() => {
    const description = systemSettings?.general?.system_description;
    if (!description) return;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = description;
  }, [systemSettings]);

  return null;
}

export default function App() {
  if (isPlatformHost) {
    return <PlatformApp />;
  }
  if (isMarketingHost) {
    return <MarketingApp />;
  }
  return <TenantApp />;
}

// TenantApp is every route this app already had before this feature —
// unchanged below except for its name (it used to be App itself). Split out
// so App() can decide, before any of this even runs, whether to mount this
// tree or PlatformApp's completely separate one (see isPlatformHost's own
// doc comment above) — this tree's own AuthProvider/tenant routes must
// never even construct on the platform host, the same way PlatformApp's
// PlatformAuthProvider must never construct on a tenant's own subdomain.
function TenantApp() {
  const [systemSettings, setSystemSettings] = useState({});
  /* const [publicConfig, setPublicConfig] = useState(() => {
    // نحاول نقرأ نسخة محفوظة مسبقًا من localStorage
    const saved = localStorage.getItem("publicConfig");
    return saved ? JSON.parse(saved) : {};
  }); */

  const [publicConfig, setPublicConfig] = useState(() => {
    try {
      const saved = localStorage.getItem("publicConfig");
      // نتحقق أن القيمة ليست null وليست نص "undefined" وليست نص "null"
      if (saved && saved !== "undefined" && saved !== "null") {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error("Error parsing publicConfig from localStorage", e);
    }
    return {}; // القيمة الافتراضية في حال الفشل
  });
  

  /* useSystemConfigWS((msg) => {
    setSystemSettings((prev) => ({
      ...prev,
      [msg.category]: {
        ...prev[msg.category],
        [msg.key]: msg.value,
      },
    }));

    notify.info(`🔄 System updated: ${msg.category}.${msg.key} = ${msg.value}`);
  }); */

/*   useSystemConfigWS((msg) => {
    if (msg.type === "system_update") {
      setSystemSettings((prev) => ({
        ...prev,
        [msg.category]: {
          ...prev[msg.category],
          [msg.key]: msg.value,
        },
      }));
      notify.info(`⚙️ System setting updated: ${msg.category}.${msg.key}`);
    }
  
    if (msg.type === "public_update") {
      setPublicConfig((prev) => {
        const updated = {
          ...prev,
          [msg.category]: {
            ...prev[msg.category],
            [msg.key]: msg.value,
          },
        };
  
        // ✅ نحفظه في localStorage مباشرة
        localStorage.setItem("publicConfig", JSON.stringify(updated));
        return updated;
      });
  
      notify.info(`🌍 Public setting updated: ${msg.category}.${msg.key}`);
    }
  }); */
  
  


  return (
    
    <AuthProvider>
      <I18nProvider>
      <SystemConfigProvider>
      <OfflineProvider>
      <BrowserRouter>
      {/* ✅ Toast container يظهر في كل الصفحات */}
      <AppToastContainer />
      <FaviconSync />
      <TitleSync />
      <MetaDescriptionSync />
      {/* Offline mode's one visible indicator (this feature's own plan
          doc) — app-wide since a queued shift-close can finish syncing
          after the cashier has already navigated away from Checkout. */}
      <OfflineBanner />
        <Routes>
          {/* ✅ Public Route */}
          <Route path="/login" element={<Login />} />

          {/* ✅ All protected pages under MainLayout */}
          <Route element={<MainLayout />}>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* System Logs — read-only audit trail, gated by its own
                "activity_log_view" permission (see ListActivityLogsHandler's
                own doc comment: it can expose every user's IP/activity, not
                just the caller's own, unlike Dashboard/Reports above). */}
            <Route
              path="/logs"
              element={
                <ProtectedRoute requiredPermission="activity_log_view">
                  <Logs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/config"
              element={
                <ProtectedRoute requiredPermission="config_view">
                  <ConfigPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute requiredPermission="profile_view">
                  <Profile />
                </ProtectedRoute>
              }
            />

            <Route
              path="/users"
              element={
                <ProtectedRoute requiredPermission="user_view">
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-user"
              element={
                <ProtectedRoute requiredPermission="user_create">
                  <AddEditUser />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-user/:id"
              element={
                <ProtectedRoute requiredPermission="user_edit">
                  <AddEditUser />
                </ProtectedRoute>
              }
            />

            <Route
              path="/groups"
              element={
                <ProtectedRoute requiredPermission="group_view">
                  <Groups />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-group"
              element={
                <ProtectedRoute requiredPermission="group_create">
                  <AddEditGroup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-group/:id"
              element={
                <ProtectedRoute requiredPermission="group_edit">
                  <AddEditGroup />
                </ProtectedRoute>
              }
            />

            <Route
              path="/permissions"
              element={
                <ProtectedRoute requiredPermission="permission_view">
                  <Permissions />
                </ProtectedRoute>
              }
            />

<Route
              path="/events"
              element={
                <ProtectedRoute requiredPermission="event_view">
                  <Events />
                </ProtectedRoute>
              }
            />

<Route
              path="/add-event"
              element={
                <ProtectedRoute requiredPermission="event_create">
                  <AddEditEvent />
                </ProtectedRoute>
              }
            />

<Route
              path="/edit-event/:id"
              element={
                <ProtectedRoute requiredPermission="event_edit">
                  <AddEditEvent />
                </ProtectedRoute>
              }
            />

<Route
  path="/dental"
  element={
    <ProtectedRoute requiredPermission="dental_view">
      <DentalViewer />
    </ProtectedRoute>
  }
/>

            {/* Geography: Countries */}
            <Route
              path="/countries"
              element={
                <ProtectedRoute requiredPermission="country_view">
                  <Countries />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-country"
              element={
                <ProtectedRoute requiredPermission="country_create">
                  <AddEditCountry />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-country/:id"
              element={
                <ProtectedRoute requiredPermission="country_edit">
                  <AddEditCountry />
                </ProtectedRoute>
              }
            />

            {/* Geography: Zones */}
            <Route
              path="/zones"
              element={
                <ProtectedRoute requiredPermission="zone_view">
                  <Zones />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-zone"
              element={
                <ProtectedRoute requiredPermission="zone_create">
                  <AddEditZone />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-zone/:id"
              element={
                <ProtectedRoute requiredPermission="zone_edit">
                  <AddEditZone />
                </ProtectedRoute>
              }
            />

            {/* Geography: Cities */}
            <Route
              path="/cities"
              element={
                <ProtectedRoute requiredPermission="city_view">
                  <Cities />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-city"
              element={
                <ProtectedRoute requiredPermission="city_create">
                  <AddEditCity />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-city/:id"
              element={
                <ProtectedRoute requiredPermission="city_edit">
                  <AddEditCity />
                </ProtectedRoute>
              }
            />

            {/* Geography: GeoZones */}
            <Route
              path="/geozones"
              element={
                <ProtectedRoute requiredPermission="geozone_view">
                  <GeoZones />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-geozone"
              element={
                <ProtectedRoute requiredPermission="geozone_create">
                  <AddEditGeoZone />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-geozone/:id"
              element={
                <ProtectedRoute requiredPermission="geozone_edit">
                  <AddEditGeoZone />
                </ProtectedRoute>
              }
            />

            {/* Modules — generic enable/disable + install registry */}
            <Route
              path="/modules"
              element={
                <ProtectedRoute requiredPermission="module_manage">
                  <Modules />
                </ProtectedRoute>
              }
            />

            {/* Custom Fields — admin-configurable extra fields per page */}
            <Route
              path="/custom-fields"
              element={
                <ProtectedRoute requiredPermission="customfield_manage">
                  <CustomFields />
                </ProtectedRoute>
              }
            />

            {/* Localization — Languages CRUD + the Translations editor,
                both gated by the same language_view/language_edit
                permissions the backend already used before either page
                existed (see internal/routes/routes.go's own doc comment). */}
            <Route
              path="/languages"
              element={
                <ProtectedRoute requiredPermission="language_view">
                  <Languages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/translations"
              element={
                <ProtectedRoute requiredPermission="language_view">
                  <Translations />
                </ProtectedRoute>
              }
            />

            {/* POS: Branches */}
            <Route
              path="/branches"
              element={
                <ProtectedRoute requiredPermission="branch_view">
                  <Branches />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-branch"
              element={
                <ProtectedRoute requiredPermission="branch_create">
                  <AddEditBranch />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-branch/:id"
              element={
                <ProtectedRoute requiredPermission="branch_edit">
                  <AddEditBranch />
                </ProtectedRoute>
              }
            />

            {/* POS: Sections */}
            <Route
              path="/sections"
              element={
                <ProtectedRoute requiredPermission="section_view">
                  <Sections />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-section"
              element={
                <ProtectedRoute requiredPermission="section_create">
                  <AddEditSection />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-section/:id"
              element={
                <ProtectedRoute requiredPermission="section_edit">
                  <AddEditSection />
                </ProtectedRoute>
              }
            />

            {/* POS: Tables */}
            <Route
              path="/tables"
              element={
                <ProtectedRoute requiredPermission="table_view">
                  <Tables />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-table"
              element={
                <ProtectedRoute requiredPermission="table_create">
                  <AddEditTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-table/:id"
              element={
                <ProtectedRoute requiredPermission="table_edit">
                  <AddEditTable />
                </ProtectedRoute>
              }
            />

            {/* POS: Categories */}
            <Route
              path="/categories"
              element={
                <ProtectedRoute requiredPermission="category_view">
                  <Categories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-category"
              element={
                <ProtectedRoute requiredPermission="category_create">
                  <AddEditCategory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-category/:id"
              element={
                <ProtectedRoute requiredPermission="category_edit">
                  <AddEditCategory />
                </ProtectedRoute>
              }
            />

            {/* POS: Units of Measure */}
            <Route
              path="/units"
              element={
                <ProtectedRoute requiredPermission="unit_view">
                  <Units />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-unit"
              element={
                <ProtectedRoute requiredPermission="unit_create">
                  <AddEditUnit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-unit/:id"
              element={
                <ProtectedRoute requiredPermission="unit_edit">
                  <AddEditUnit />
                </ProtectedRoute>
              }
            />

            {/* POS: Taxes */}
            <Route
              path="/taxes"
              element={
                <ProtectedRoute requiredPermission="tax_view">
                  <Taxes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-tax"
              element={
                <ProtectedRoute requiredPermission="tax_create">
                  <AddEditTax />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-tax/:id"
              element={
                <ProtectedRoute requiredPermission="tax_edit">
                  <AddEditTax />
                </ProtectedRoute>
              }
            />

            {/* POS: Tax Groups */}
            <Route
              path="/tax-groups"
              element={
                <ProtectedRoute requiredPermission="taxgroup_view">
                  <TaxGroups />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-tax-group"
              element={
                <ProtectedRoute requiredPermission="taxgroup_create">
                  <AddEditTaxGroup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-tax-group/:id"
              element={
                <ProtectedRoute requiredPermission="taxgroup_edit">
                  <AddEditTaxGroup />
                </ProtectedRoute>
              }
            />

            {/* POS: Discounts (Sales — bulk/group discount campaigns) */}
            <Route
              path="/discounts"
              element={
                <ProtectedRoute requiredPermission="sale_view">
                  <Discounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-sale"
              element={
                <ProtectedRoute requiredPermission="sale_create">
                  <AddEditSale />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-sale/:id"
              element={
                <ProtectedRoute requiredPermission="sale_edit">
                  <AddEditSale />
                </ProtectedRoute>
              }
            />

            {/* POS: Bundle Offers ("buy N, cheapest one free") */}
            <Route
              path="/bundle-offers"
              element={
                <ProtectedRoute requiredPermission="bundleoffer_view">
                  <BundleOffers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-bundle-offer"
              element={
                <ProtectedRoute requiredPermission="bundleoffer_create">
                  <AddEditBundleOffer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-bundle-offer/:id"
              element={
                <ProtectedRoute requiredPermission="bundleoffer_edit">
                  <AddEditBundleOffer />
                </ProtectedRoute>
              }
            />

            {/* POS: Currencies */}
            <Route
              path="/currencies"
              element={
                <ProtectedRoute requiredPermission="currency_view">
                  <Currencies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-currency"
              element={
                <ProtectedRoute requiredPermission="currency_create">
                  <AddEditCurrency />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-currency/:id"
              element={
                <ProtectedRoute requiredPermission="currency_edit">
                  <AddEditCurrency />
                </ProtectedRoute>
              }
            />
            <Route
              path="/currencies/:id/denominations"
              element={
                <ProtectedRoute requiredPermission="currency_view">
                  <CurrencyDenominations />
                </ProtectedRoute>
              }
            />

            {/* POS: Checkout */}
            <Route
              path="/checkout"
              element={
                <ProtectedRoute requiredPermission="order_create">
                  <Checkout />
                </ProtectedRoute>
              }
            />

            {/* Offline mode's sync-queue review screen (this feature's own
                plan doc) — same permission as Checkout itself, since this is
                purely a cashier-facing view of THEIR OWN till's local queue,
                not an admin/reporting feature. */}
            <Route
              path="/checkout/offline-queue"
              element={
                <ProtectedRoute requiredPermission="order_create">
                  <OfflineQueueReview />
                </ProtectedRoute>
              }
            />

            {/* POS: Orders */}
            <Route
              path="/orders"
              element={
                <ProtectedRoute requiredPermission="order_view">
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/:id"
              element={
                <ProtectedRoute requiredPermission="order_view">
                  <OrderDetail />
                </ProtectedRoute>
              }
            />

            {/* POS: Kitchen Display — gated by "order_kitchen_update", not
                "order_view" (see ListKitchenOrdersHandler's own doc comment
                in order_handler.go). */}
            <Route
              path="/kitchen-display"
              element={
                <ProtectedRoute requiredPermission="order_kitchen_update">
                  <KitchenDisplay />
                </ProtectedRoute>
              }
            />

            {/* POS: Shifts */}
            <Route
              path="/shifts"
              element={
                <ProtectedRoute requiredPermission="shift_view">
                  <Shifts />
                </ProtectedRoute>
              }
            />

            {/* POS: Purchase Vouchers */}
            <Route
              path="/vouchers"
              element={
                <ProtectedRoute requiredPermission="voucher_view">
                  <Vouchers />
                </ProtectedRoute>
              }
            />

            {/* Reports — no single requiredPermission here on purpose: viewing
                is gated per report ("report_view_<key>", see
                internal/routes/routes.go's own doc comment), so the picker
                itself is reachable by any authenticated user and just shows
                whichever reports they hold a permission for (possibly none) —
                the same way ListReportDefinitionsHandler filters its list
                server-side rather than 403ing the whole page. */}
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <Reports />
                </ProtectedRoute>
              }
            />

            {/* POS: Customers */}
            <Route
              path="/customers"
              element={
                <ProtectedRoute requiredPermission="customer_view">
                  <Customers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-customer"
              element={
                <ProtectedRoute requiredPermission="customer_create">
                  <AddEditCustomer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-customer/:id"
              element={
                <ProtectedRoute requiredPermission="customer_edit">
                  <AddEditCustomer />
                </ProtectedRoute>
              }
            />

            {/* POS: Suppliers */}
            <Route
              path="/suppliers"
              element={
                <ProtectedRoute requiredPermission="supplier_view">
                  <Suppliers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-supplier"
              element={
                <ProtectedRoute requiredPermission="supplier_create">
                  <AddEditSupplier />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-supplier/:id"
              element={
                <ProtectedRoute requiredPermission="supplier_edit">
                  <AddEditSupplier />
                </ProtectedRoute>
              }
            />

            {/* POS: Purchase Orders */}
            <Route
              path="/purchase-orders"
              element={
                <ProtectedRoute requiredPermission="purchaseorder_view">
                  <PurchaseOrders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-purchase-order"
              element={
                <ProtectedRoute requiredPermission="purchaseorder_create">
                  <AddPurchaseOrder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase-orders/:id"
              element={
                <ProtectedRoute requiredPermission="purchaseorder_view">
                  <ViewPurchaseOrder />
                </ProtectedRoute>
              }
            />

            {/* POS: Stock Transfers */}
            <Route
              path="/stock-transfers"
              element={
                <ProtectedRoute requiredPermission="stocktransfer_view">
                  <StockTransfers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-stock-transfer"
              element={
                <ProtectedRoute requiredPermission="stocktransfer_create">
                  <AddStockTransfer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stock-transfers/:id"
              element={
                <ProtectedRoute requiredPermission="stocktransfer_view">
                  <ViewStockTransfer />
                </ProtectedRoute>
              }
            />

            {/* POS: Products */}
            <Route
              path="/products"
              element={
                <ProtectedRoute requiredPermission="product_view">
                  <Products />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-product"
              element={
                <ProtectedRoute requiredPermission="product_create">
                  <AddEditProduct />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-product/:id"
              element={
                <ProtectedRoute requiredPermission="product_edit">
                  <AddEditProduct />
                </ProtectedRoute>
              }
            />

            {/* POS: Inventory */}
            <Route
              path="/inventory"
              element={
                <ProtectedRoute requiredPermission="inventory_view">
                  <Inventory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stock-movements"
              element={
                <ProtectedRoute requiredPermission="inventory_view">
                  <StockMovements />
                </ProtectedRoute>
              }
            />

            {/* Accounting: Chart of Accounts */}
            <Route
              path="/accounts"
              element={
                <ProtectedRoute requiredPermission="account_view">
                  <ChartOfAccounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-account"
              element={
                <ProtectedRoute requiredPermission="account_create">
                  <AddEditAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-account/:id"
              element={
                <ProtectedRoute requiredPermission="account_edit">
                  <AddEditAccount />
                </ProtectedRoute>
              }
            />

            {/* Accounting: Journal Entries — manual entries only; never
                edited once posted, so there is no "/edit-journal-entry" route
                (see AddJournalEntry's own doc comment). */}
            <Route
              path="/journal-entries"
              element={
                <ProtectedRoute requiredPermission="journalentry_view">
                  <JournalEntries />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-journal-entry"
              element={
                <ProtectedRoute requiredPermission="journalentry_create">
                  <AddJournalEntry />
                </ProtectedRoute>
              }
            />

            {/* Accounting: Expense Categories */}
            <Route
              path="/expense-categories"
              element={
                <ProtectedRoute requiredPermission="expensecategory_view">
                  <ExpenseCategories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-expense-category"
              element={
                <ProtectedRoute requiredPermission="expensecategory_create">
                  <AddEditExpenseCategory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-expense-category/:id"
              element={
                <ProtectedRoute requiredPermission="expensecategory_edit">
                  <AddEditExpenseCategory />
                </ProtectedRoute>
              }
            />

            {/* Accounting: Expenses — each one auto-posts its own journal
                entry on creation; never edited once recorded, so there is no
                "/edit-expense" route (see AddExpense's own doc comment). */}
            <Route
              path="/expenses"
              element={
                <ProtectedRoute requiredPermission="expense_view">
                  <Expenses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-expense"
              element={
                <ProtectedRoute requiredPermission="expense_create">
                  <AddExpense />
                </ProtectedRoute>
              }
            />

            {/* Services & Appointments module — see
                service_handler.go/models/service.go. Staff assignments
                (who can perform a service, at what commission) are managed
                inline on this same edit screen, not a separate route. */}
            <Route
              path="/services"
              element={
                <ProtectedRoute requiredPermission="service_view">
                  <Services />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-service"
              element={
                <ProtectedRoute requiredPermission="service_create">
                  <AddEditService />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-service/:id"
              element={
                <ProtectedRoute requiredPermission="service_edit">
                  <AddEditService />
                </ProtectedRoute>
              }
            />

            {/* Appointments — the booking calendar, its own resource
                separate from Service (see models.Appointment's own doc
                comment). A single screen (day-view calendar with a
                booking/edit modal) rather than list+add/edit routes like
                most other resources here. */}
            <Route
              path="/appointments"
              element={
                <ProtectedRoute requiredPermission="appointment_view">
                  <Appointments />
                </ProtectedRoute>
              }
            />

            {/* Thermal Receipt Printing (ESC/POS) via Print-Agent — see
                models.PrintAgent/Printer/ReceiptTemplate on the backend and
                this feature's own Phase 1 plan. print_receipt itself has
                no dedicated route — it's OrderDetail.js's own manual print
                button, hitting POST /orders/:id/print directly. */}
            <Route
              path="/print-agents"
              element={
                <ProtectedRoute requiredPermission="printer_view">
                  <PrintAgents />
                </ProtectedRoute>
              }
            />
            {/* Local Print Setup — a PER-TILL screen, not an admin one (see
                LocalPrintSetup.js's own doc comment): gated on
                "print_receipt", the same permission the manual Print
                Receipt button already uses, deliberately separate from
                "printer_manage" so an ordinary cashier can set up their own
                till without needing full printer-admin rights. */}
            <Route
              path="/local-print-setup"
              element={
                <ProtectedRoute requiredPermission="print_receipt">
                  <LocalPrintSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/printers"
              element={
                <ProtectedRoute requiredPermission="printer_view">
                  <Printers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-printer"
              element={
                <ProtectedRoute requiredPermission="printer_manage">
                  <AddEditPrinter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-printer/:id"
              element={
                <ProtectedRoute requiredPermission="printer_manage">
                  <AddEditPrinter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/receipt-template"
              element={
                <ProtectedRoute requiredPermission="printer_view">
                  <ReceiptTemplateSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/print-jobs"
              element={
                <ProtectedRoute requiredPermission="printer_view">
                  <PrintJobs />
                </ProtectedRoute>
              }
            />
            {/* Print Barcodes — bulk product-label printing (see
                internal/handlers/label_print_handler.go). Gated by
                print_labels, separate from printer_view/printer_manage
                above, same reasoning as print_receipt's own separate gate:
                someone who prints labels day-to-day (e.g. an inventory
                clerk) doesn't also need printer/agent management access. */}
            <Route
              path="/print-barcodes"
              element={
                <ProtectedRoute requiredPermission="print_labels">
                  <PrintBarcodes />
                </ProtectedRoute>
              }
            />

            <Route path="/denied" element={<AccessDenied />} />

            {/* Default */}
            <Route
              path="*"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
      </OfflineProvider>
      </SystemConfigProvider>
      </I18nProvider>
    </AuthProvider>
  );
}
